import { z } from "zod";
import {
  Count,
  Currency,
  EntitlementKind,
  IsoDate,
  IsoDateTime,
  LedgerType,
  Money,
  NonNegativeMoney,
  paged,
  PageQuery,
  Period,
  Uuid,
} from "./common.ts";
import { OrderProduct } from "./orders.ts";

/*
 * Usage and billing (07 §2.2, §3.3 to §3.6, 06 `usage_item`, `ledger_entry`, `usage_report`,
 * UC-12 to UC-16, UC-25). Everything in USD (ADR-058); the conversion to EUR happens on the
 * invoice outside the platform. Open amounts and sums are computed, never stored (Grundsatz 2).
 */

/** `GET /api/usage/summary?period=YYYY-MM`; without `period` the current month. */
export const UsageSummaryQuery = z.object({
  period: Period.optional(),
});
export type UsageSummaryQuery = z.infer<typeof UsageSummaryQuery>;

/**
 * What OpenRouter reports for the entitlement's key so far this month, before any booking (G.1b,
 * TL 2026-10-14): the booked items reach yesterday at best and the last three days are still
 * open, so the view shows this next to them, provisional. Read by the API when the summary of the
 * current month is asked for, kept up to five minutes; never booked.
 */
export const LiveUsage = z.object({
  /** OpenRouter's `usage_monthly` of the active key: provider cost without markup, USD */
  cost: NonNegativeMoney,
  /** cost plus markup once MARKUP_PERCENT is set (R-06, F-18); null while it is not */
  amount: NonNegativeMoney.nullable(),
  /** when the API read it from OpenRouter */
  asOf: IsoDateTime,
});
export type LiveUsage = z.infer<typeof LiveUsage>;

/** Usage of one entitlement in the period, separated by demo (07 §3.4). */
export const UsageByProduct = z.object({
  entitlementId: Uuid,
  product: OrderProduct,
  kind: EntitlementKind,
  monthlyLimit: NonNegativeMoney,
  /**
   * runs over the platform's AI keys (ADR-144), as `Entitlement.aiAccess`: its order's AI access,
   * without an order its product's. Without it there is no monthly limit (zero), and the portal
   * shows "kein Monatslimit" in its place (ADR-144 addendum of 2026-10-30)
   */
  aiAccess: z.boolean(),
  /** provider cost before markup, from the usage items */
  baseCost: NonNegativeMoney,
  /** share of the service markup (R-06, F-18) */
  markup: NonNegativeMoney,
  /** base cost plus markup, what the ledger books */
  amount: NonNegativeMoney,
  /** false for demo usage: booked, reported, never invoiced (R-07) */
  billable: z.boolean(),
  requests: Count,
  promptTokens: Count,
  completionTokens: Count,
  reasoningTokens: Count,
  /**
   * The running month live and provisional (G.1b); null for another period, without an active
   * key, or when OpenRouter does not answer in time. Not part of the report.
   */
  live: LiveUsage.nullable(),
  /**
   * The available amount (ADR-149): `monthlyLimit` minus `live.amount`, what the key may still use
   * this month. Null while `live` or its amount is null, so for another period too; negative as it
   * is, after a lowered limit for one.
   */
  available: Money.nullable(),
});
export type UsageByProduct = z.infer<typeof UsageByProduct>;

export const UsageSummaryResponse = z.object({
  period: Period,
  currency: Currency,
  /** credit limit of the customer; null while registered or suspended (GC4, TL 2026-11-02) */
  creditLimit: NonNegativeMoney.nullable(),
  /** sum of the monthly limits of all living entitlements */
  limitSum: NonNegativeMoney,
  /**
   * the live usage of the whole customer (addendum to ADR-145 of 2026-10-29), by the rule of
   * `StaffCustomerFigures.live`: the sum over the products that have one, demos included, `asOf`
   * the oldest reading, `amount` null while one of them has none; null when none has live usage,
   * so for another period too
   */
  live: LiveUsage.nullable(),
  /**
   * `limitSum` minus `live.amount` (ADR-149); null while `live` or its amount is null; negative as
   * it is
   */
  available: Money.nullable(),
  /** sum of all billable ledger entries of the customer, across periods (07 §3.4) */
  openAmount: Money,
  products: z.array(UsageByProduct),
});
export type UsageSummaryResponse = z.infer<typeof UsageSummaryResponse>;

/** One usage item as OpenRouter reports it: per key, day, model and endpoint (06 `usage_item`). */
export const UsageItem = z.object({
  id: Uuid,
  entitlementId: Uuid,
  /** day in UTC */
  usageDate: IsoDate,
  model: z.string(),
  modelPermaslug: z.string(),
  endpointId: z.string(),
  providerName: z.string(),
  requests: Count,
  promptTokens: Count,
  completionTokens: Count,
  reasoningTokens: Count,
  /** provider cost before markup */
  cost: NonNegativeMoney,
  /** true once the day is closed; then immutable */
  isFinal: z.boolean(),
});
export type UsageItem = z.infer<typeof UsageItem>;

/** `GET /api/usage/items`, newest day first. */
export const UsageItemsQuery = PageQuery.extend({
  period: Period.optional(),
  entitlementId: Uuid.optional(),
});
export type UsageItemsQuery = z.infer<typeof UsageItemsQuery>;

export const UsageItemsResponse = paged(UsageItem);
export type UsageItemsResponse = z.infer<typeof UsageItemsResponse>;

/**
 * An entry of the ledger (06 `ledger_entry`, R-05). Usage positive, corrections signed, an invoice
 * minus the open amount of its month: negative, positive for a credit note (AP-B11.3).
 */
export const LedgerEntry = z.object({
  id: Uuid,
  customerId: Uuid,
  type: LedgerType,
  amount: Money,
  baseCost: NonNegativeMoney.nullable(),
  markup: NonNegativeMoney.nullable(),
  billable: z.boolean(),
  entitlementId: Uuid.nullable(),
  usageItemId: Uuid.nullable(),
  /** the month the entry is booked into, the one report and invoice count it in */
  period: Period,
  /**
   * on a correction the month it corrects, never later than `period`; earlier when that month was
   * frozen and the API booked into the first open one (ADR-138 point 3); null on every other entry
   */
  correctedPeriod: Period.nullable(),
  invoiceReference: z.string().nullable(),
  /** mandatory on corrections */
  reason: z.string().nullable(),
  /** staff member on corrections and invoices */
  createdBy: Uuid.nullable(),
  createdAt: IsoDateTime,
});
export type LedgerEntry = z.infer<typeof LedgerEntry>;

/** Usage of one product in the report, demo separated and marked non-billable (08 §7 rule 2). */
export const UsageReportProduct = UsageByProduct.pick({
  entitlementId: true,
  product: true,
  kind: true,
  baseCost: true,
  markup: true,
  amount: true,
  billable: true,
  requests: true,
  promptTokens: true,
  completionTokens: true,
  reasoningTokens: true,
});

/** Content of a usage report (06 `usage_report.content`, REQ-24). */
export const UsageReportContent = z.object({
  period: Period,
  currency: Currency,
  products: z.array(UsageReportProduct),
  /** corrections booked into the period, those of an earlier frozen month included (`correctedPeriod`) */
  corrections: z.array(LedgerEntry),
  totals: z.object({
    baseCost: NonNegativeMoney,
    markup: NonNegativeMoney,
    /** all usage of the period including demo */
    amount: NonNegativeMoney,
    /** usage that is invoiced */
    billableAmount: NonNegativeMoney,
    /** demo usage, shown and never invoiced (R-07) */
    nonBillableAmount: NonNegativeMoney,
    /** billable usage plus corrections; what the invoice covers */
    openAmount: Money,
  }),
});
export type UsageReportContent = z.infer<typeof UsageReportContent>;

export const UsageReport = z.object({
  id: Uuid,
  customerId: Uuid,
  period: Period,
  content: UsageReportContent,
  /** set with the invoice booking; afterwards the report is frozen (07 §3.6) */
  invoiceLedgerEntryId: Uuid.nullable(),
  /** when the invoice was booked, for "abgerechnet am …" (M13); null until then */
  invoicedAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
  frozenAt: IsoDateTime.nullable(),
});
export type UsageReport = z.infer<typeof UsageReport>;

/** `GET /api/usage/reports/:period` (UC-13), the customer's report of that month. */
export const UsageReportResponse = UsageReport;
export type UsageReportResponse = z.infer<typeof UsageReportResponse>;
